/**
 * Headless helpers for the carriage cost pages. Pure functions over the
 * carriage-cost types; no React, no DOM.
 */

import type {
  AllocationSplit,
  Carrier,
  CourierInvoiceStatus,
  MatchedBy,
} from "../data/carriage-cost-types";

export const CARRIER_LABELS: Record<Carrier, string> = {
  dx: "DX",
  dpd: "DPD",
};

export const MATCHED_BY_LABELS: Record<MatchedBy | "unallocated", string> = {
  delivery_note: "Delivery note",
  job_number: "Job number",
  sales_order: "Sales order",
  manual: "Resolved by hand",
  unallocated: "Unmatched",
};

export const INVOICE_STATUS_LABELS: Record<CourierInvoiceStatus, string> = {
  parsed: "Parsed",
  reviewed: "Reviewed",
  posted: "Posted",
  partially_posted: "Partially posted",
};

/** Consignments placed automatically or by hand, as a share of the invoice. */
export function allocatedShare(split: AllocationSplit): {
  count: number;
  value: number;
  countPct: number;
  valuePct: number;
} {
  const tiers = [
    split.delivery_note,
    split.job_number,
    split.sales_order,
    split.manual,
  ];
  const count = tiers.reduce((n, t) => n + t.count, 0);
  const value = tiers.reduce((v, t) => v + t.value, 0);
  const totalCount = count + split.unallocated.count;
  const totalValue = value + split.unallocated.value;
  return {
    count,
    value,
    countPct: totalCount ? (100 * count) / totalCount : 0,
    valuePct: totalValue ? (100 * value) / totalValue : 0,
  };
}

/** Ordered tiers for the split bar, unmatched last. */
export function splitSegments(
  split: AllocationSplit,
): { key: MatchedBy | "unallocated"; count: number; value: number }[] {
  return [
    { key: "delivery_note", ...split.delivery_note },
    { key: "job_number", ...split.job_number },
    { key: "sales_order", ...split.sales_order },
    { key: "manual", ...split.manual },
    { key: "unallocated", ...split.unallocated },
  ];
}

/** `YYYY-MM` → "Aug 2026". */
export function formatMonthKey(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatPct(value: number | null, digits = 0): string {
  if (value === null || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

/** Recovery tone: green ≥ 90, amber 70–90, red below. */
export function recoveryTone(
  pct: number | null,
): "good" | "warn" | "bad" | "none" {
  if (pct === null) return "none";
  if (pct >= 90) return "good";
  if (pct >= 70) return "warn";
  return "bad";
}
